import { setCookie } from "@/lib/setCookie";
import { ROLE_OWNER } from "@/lib/utils";
import { reset, setCommunityData, setCurrentFinanceYear, setTncStatus } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { useGetBomUserCommunities, useGetCommunityContract, useUpdateUserTncMutationMutation } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {loggedInUserData, currentRole, currentFinanceYear, communityData, prevCommunityData} = useSelector(
    (state: RootState) => state.currentData
  );
  const [openTncDialog, setOpenTncDialog] = useState<boolean>(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!currentFinanceYear) {
      dispatch(setCurrentFinanceYear(String(moment().year())));
    }
  }, []);

  // const {data: tncApprovedData, isLoading: isLoadingTncApprovedData} = useGetApprovedTncByUserAndCommunity({
  //   userId: loggedInUserData?.userId,
  //   communityId: communityData?.communityId,
  //   queryParams: {
  //     enabled: !!(loggedInUserData?.userId && communityData?.communityId)
  //   }
  // });

  const { data: bomUserCommunities, isLoading: isBomCommunityLoading } =
    useGetBomUserCommunities({
      enabled: loggedInUserData?.roles?.includes(ROLE_OWNER),
    });
    
  const {data: contract, refetch: refetchCommunityContract, isRefetching: isContractLoading} = useGetCommunityContract({
    id: communityData?.communityId,
    queryParams: {
      enabled: false
    }
  })
  const formattedTncContent = contract?.data?.contentRichText?.replace(
    /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/g,
    (date: string, day: string, month: string, year: string) => {
      const parsedDate = moment(`${day}/${month}/${year}`, "DD/MM/YYYY", true);
      return parsedDate.isValid() ? parsedDate.format("MM/DD/YYYY") : date;
    }
  );
  let contentRichText = formattedTncContent;

  if (formattedTncContent) {
    const tncDocument = new DOMParser().parseFromString(
      formattedTncContent,
      "text/html"
    );
    let hasEmailProtectLink = false;

    tncDocument.querySelectorAll("a").forEach((link) => {
      if (/email\s*protect/i.test(link.textContent?.replace(/\s+/g, " ").trim() || "")) {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        hasEmailProtectLink = true;
      }
    });

    if (hasEmailProtectLink) {
      contentRichText = tncDocument.documentElement.outerHTML;
    }
  }

  const { mutate: updateTnc, isPending: isUpdatePending } =
    useUpdateUserTncMutationMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenTncDialog(false);
        dispatch(setCommunityData({...communityData, tncApproved: true}));
        queryClient.cancelQueries({queryKey: ["community-contract", communityData?.communityId]})
      },
    });

  // useEffect(() => {
  //   if (tncApprovedData?.data) {
  //     if (!(tncApprovedData?.data?.length && tncApprovedData?.data?.[0]?.approved)) {
  //       refetchCommunityContract();
  //     }
  //   }
  // }, [tncApprovedData])

  useEffect(() => {
    if (!communityData?.tncApproved && communityData?.communityId) {
      refetchCommunityContract();
    }
  }, [communityData])

  useEffect(() => {
    if (contract?.data && !communityData?.tncApproved) {
      setOpenTncDialog(true);
    }
  }, [contract])

  const onDisAgree = () => {
    if (bomUserCommunities?.data?.length > 1) {
      const foundAcceptedTnc = bomUserCommunities?.data?.some((data: any) => Number(data.tncApproved));
      if (foundAcceptedTnc) {
        dispatch(setCommunityData(prevCommunityData));
        dispatch(setTncStatus("decline" as any));
        setOpenTncDialog(false);
      } else {
        dispatch(reset());
        queryClient.clear();
        setCookie("accessToken", "");
        navigate(`/`);
      }
    } else {
      dispatch(reset());
      queryClient.clear();
      setCookie("accessToken", "");
      navigate(`/`);
    }
  }

  const onAgree = () => {
    updateTnc({
      data: {
        userId: loggedInUserData?.userId,
        communityInstanceId: contract?.data?.communityInstanceId
      }
    })
  }

  return {
    currentFinanceYear,
    loggedInUserData,
    currentRole,
    isBomCommunityLoading,
    isContractLoading,
    contract,
    contentRichText,
    openTncDialog,
    setOpenTncDialog,
    onDisAgree,
    onAgree,
    isUpdatePending
  };
};

export default useDashboard;
